import { Head, Link, router } from '@inertiajs/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { PaginationType, TaxPayerFiltersType, TaxPayerType } from './types.ts'
import {Button} from "@/components/ui/button.tsx";
import {MapPin, Plus, Search, Trash2} from "lucide-react";
import {Card} from "@/components/ui/card.tsx";
import {Input} from "@/components/ui/input.tsx";
import Pagination from "@/components/pagination.tsx";

interface IndexProps {
  tax_payers: TaxPayerType[]
  filters: TaxPayerFiltersType
  pagination: PaginationType
  can_create: boolean
  can_destroy?: boolean
}

export default function Index({ tax_payers, filters, pagination, can_create, can_destroy = false }: IndexProps) {
  const hasInitialized = useRef(false)
  const [term, setTerm] = useState(filters.term)

  useEffect(() => {
    setTerm(filters.term)
  }, [filters.term])

  const query = useMemo(() => ({ term }), [term])

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true
      return
    }

    if (term === filters.term) {
      return
    }

    const timeoutId = setTimeout(() => {
      router.get("/tax_payers", { ...query, page: 1 }, {
        preserveState: true,
        preserveScroll: true,
        replace: true
      })
    }, 350)

    return () => clearTimeout(timeoutId)
  }, [query, term, filters.term])

  const handleDestroy = (taxpayer: TaxPayerType) => {
    if (!window.confirm(`Supprimer le contribuable "${taxpayer.name}" ?`)) return

    router.delete(`/tax_payers/${taxpayer.id}`, {
      preserveScroll: true,
    })
  }

  return (
    <>
      <Head title="Contribuables" />
      <div className="flex flex-col gap-6 p-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Les contribuables</h1>
            <p className="text-sm text-muted-foreground">Gestion des contribuables</p>
          </div>
          {can_create && (
            <Link href="/tax_payers/new">
              <Button className="gap-2">
                <Plus className="h-4 w-4" />
                Ajouter un contribuable
              </Button>
            </Link>
          )}
        </div>

        {/* Search and Filters */}
        <Card className="border-border bg-card p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom, raison sociale ou code..."
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </Card>

        {/* Contribuables Table */}
        <Card className="border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-border">
              <tr className="text-left">
                <th className="p-4 text-sm font-medium text-muted-foreground">Contribuable</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Fiscal ID</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Zone</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Catégorie</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Adresse</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Balance</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Statut</th>
                <th className="p-4 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
              </thead>
              <tbody>
              {tax_payers.map((taxpayer) => (
                  <tr key={taxpayer.id} className="border-b border-border transition-colors hover:bg-secondary/50">
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-foreground">{taxpayer.name}</p>
                        <p className="text-sm text-muted-foreground">{taxpayer.phone}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="font-mono text-sm text-foreground">{taxpayer.code}</p>
                    </td>
                    <td className="p-4">
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                      <MapPin className="h-3 w-3" />
                      {taxpayer.area_name}
                    </span>
                    </td>
                    <td className="p-4">
                    <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-medium text-foreground">
                      {taxpayer.tax_payer_type_name}
                    </span>
                    </td>
                    <td className="p-4">
                      <div className="block text-sm text-muted-foreground w-72">
                        <p className="text-sm text-foreground">{taxpayer.address}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <p
                          className={`font-mono text-sm font-semibold ${
                              Number(taxpayer.balance_due || 0) > 0 ? "text-warning" : "text-success"
                          }`}
                      >
                        {Number(taxpayer.balance_due || 0).toFixed(2)}
                      </p>
                    </td>
                    <td className="p-4">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                          taxpayer.has_outstanding_balance
                            ? "bg-warning/10 text-warning"
                            : "bg-success/10 text-success"
                        }`}
                      >
                        {taxpayer.has_outstanding_balance ? "Outstanding" : "Cleared"}
                      </span>
                    </td>
                    <td className="p-4">
                      <Link href={`/tax_payers/${taxpayer.id}`}>
                        <button className="text-sm font-medium text-primary hover:text-primary/80 transition-colors">Voir les détails</button>
                      </Link>
                      {can_destroy && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="ml-2 text-destructive hover:text-destructive"
                          aria-label={`Supprimer ${taxpayer.name}`}
                          onClick={() => handleDestroy(taxpayer)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
              ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/*<Card className="border-border bg-card p-4">*/}
        {/*  <div className="overflow-x-auto">*/}
        {/*    <table className="w-full">*/}
        {/*      <thead>*/}
        {/*        <tr className="border-b border-border text-left text-sm font-medium text-muted-foreground">*/}
        {/*          <th className="pb-3 pr-4">Code</th>*/}
        {/*          <th className="pb-3 pr-4">Nom</th>*/}
        {/*          <th className="pb-3 pr-4">Full name</th>*/}
        {/*          <th className="pb-3 pr-4">Community</th>*/}
        {/*          <th className="pb-3 pr-4">Zone</th>*/}
        {/*          <th className="pb-3 pr-4">Type de contribuable</th>*/}
        {/*          <th className="pb-3 pr-4">Téléphone</th>*/}
        {/*          <th className="pb-3 pr-4">Email</th>*/}
        {/*          <th className="pb-3 pr-4">Verified</th>*/}
        {/*          <th className="pb-3">Actions</th>*/}
        {/*        </tr>*/}
        {/*      </thead>*/}
        {/*      <tbody>*/}
        {/*        {tax_payers.map((payer) => (*/}
        {/*          <tr key={payer.id} className="border-b border-border text-sm">*/}
        {/*            <td className="py-3 pr-4 font-mono text-foreground">{payer.code || "-"}</td>*/}
        {/*            <td className="py-3 pr-4 text-foreground">{payer.name || "-"}</td>*/}
        {/*            <td className="py-3 pr-4 text-foreground">{payer.full_name || "-"}</td>*/}
        {/*            <td className="py-3 pr-4 text-muted-foreground">{payer.community_name || payer.community_id || "-"}</td>*/}
        {/*            <td className="py-3 pr-4 text-muted-foreground">{payer.area_name || payer.area_id || "-"}</td>*/}
        {/*            <td className="py-3 pr-4 text-muted-foreground">{payer.tax_payer_type_name || payer.tax_payer_type_id || "-"}</td>*/}
        {/*            <td className="py-3 pr-4 text-muted-foreground">{payer.phone || "-"}</td>*/}
        {/*            <td className="py-3 pr-4 text-muted-foreground">{payer.email || "-"}</td>*/}
        {/*            <td className="py-3 pr-4">*/}
        {/*              <span className={payer.verified ? "text-success" : "text-muted-foreground"}>*/}
        {/*                {payer.verified ? "Yes" : "No"}*/}
        {/*              </span>*/}
        {/*            </td>*/}
        {/*            <td className="py-3">*/}
        {/*              <Link href={`/tax_payers/${payer.id}`}>*/}
        {/*                <Button size="sm" variant="outline">Voir</Button>*/}
        {/*              </Link>*/}
        {/*            </td>*/}
        {/*          </tr>*/}
        {/*        ))}*/}
        {/*      </tbody>*/}
        {/*    </table>*/}
        {/*  </div>*/}
        {/*</Card>*/}

        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.total_pages}
          basePath="/tax_payers"
          query={query}
        />
      </div>
    </>
  )
}
